import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/loomi/GoogleSignInButton";

const CONFIG = {
  admin: {
    label: "Admin console",
    role: "admin",
    accent: "from-indigo-500/30 to-fuchsia-500/10",
    demo: { email: "prakash@synclineresearch.com", password: "Admin@123" },
    home: "/admin",
    switch: [
      { to: "/panel/login", label: "Panel login" },
    ],
    testPrefix: "admin",
  },
  pm: {
    label: "Project Manager",
    role: "pm",
    accent: "from-fuchsia-500/30 to-indigo-500/10",
    demo: { email: "pm@synclineresearch.com", password: "Pm@12345" },
    home: "/pm",
    switch: [],
    testPrefix: "pm",
  },
  panel: {
    label: "Panelist",
    role: "panel",
    accent: "from-emerald-500/30 to-sky-500/10",
    demo: { email: "panel@synclineresearch.com", password: "Panel@123" },
    home: "/panel",
    switch: [
      { to: "/login", label: "Admin login" },
    ],
    testPrefix: "panel",
  },
  client: {
    label: "Client (vendor)",
    role: "client",
    accent: "from-sky-500/30 to-indigo-500/10",
    demo: { email: "client@synclineresearch.com", password: "Client@123" },
    home: "/client",
    switch: [],
    testPrefix: "client",
  },
};

export default function Login({ variant = "admin" }) {
  const cfg = CONFIG[variant];
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(false);
  const [otp, setOtp] = useState("");

  const doLogin = async (otpCode) => {
    setLoading(true);
    try {
      // Payload intentionally omits `role` — one login form accepts any operator/panelist,
      // and we route them to the appropriate home based on the returned user.role.
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
        otp_code: otpCode || undefined,
      });
      if (data?.requires_2fa) {
        setOtpChallenge(true);
        setLoading(false);
        toast.info("Enter your 6-digit authenticator code");
        return;
      }
      localStorage.setItem("loomi_token", data.access_token);
      const homeByRole = { admin: "/admin", pm: "/pm", panel: "/panel", client: "/client" };
      const target = homeByRole[data?.user?.role] || cfg.home;
      // eslint-disable-next-line
      window.location.href = target;
    } catch (e) {
      setLoading(false);
      toast.error("Login failed", { description: formatApiError(e) });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    doLogin();
  };
  const onOtpSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    doLogin(otp);
  };

  const useDemo = () => {
    // Retained for internal E2E test hooks; UI button removed per product decision.
    setEmail(cfg.demo.email); setPassword(cfg.demo.password);
  };
  // silence unused-var warning without changing behaviour
  void useDemo;

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden flex" data-testid={`${cfg.testPrefix}-login-page`}>
      <div className="aurora" />
      <div className="relative z-10 flex-1 grid md:grid-cols-2">
        {/* Left copy */}
        <div className="hidden md:flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2" data-testid={`${cfg.testPrefix}-login-logo`}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <span className="font-display font-extrabold text-xl">SyncLine</span>
          </Link>
          <div>
            <div className="text-xs uppercase tracking-widest text-mute mb-3">{cfg.label}</div>
            <h1 className="font-display font-extrabold text-4xl lg:text-5xl leading-[0.95] max-w-md">
              Sign in to keep every respondent accounted for.
            </h1>
            <div className="mt-8 space-y-2 text-sm text-mute">
              {cfg.switch.map((s) => (
                <Link key={s.to} to={s.to} className="block hover:text-white" data-testid={`switch-${s.to.replace(/\//g,'-')}`}>→ {s.label}</Link>
              ))}
            </div>
          </div>
          <div className="text-xs text-mute">© SyncLine · redirect engine · v1</div>
        </div>

        {/* Right form */}
        <div className="flex items-center justify-center p-6 md:p-12">
          <form onSubmit={otpChallenge ? onOtpSubmit : onSubmit} className="glass rounded-2xl p-8 w-full max-w-md relative overflow-hidden">
            <div className={`absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gradient-to-br ${cfg.accent} blur-3xl`} />
            <div className="relative">
              <div className="text-xs uppercase tracking-widest text-mute mb-2">{cfg.label}</div>
              <h2 className="font-display font-extrabold text-3xl mb-6">{otpChallenge ? "Verify 2FA" : "Log in"}</h2>

              {otpChallenge ? (
                <>
                  <div className="flex items-center gap-2 mb-4 text-sm text-mute" data-testid="otp-challenge-notice">
                    <ShieldCheck className="w-4 h-4 text-indigo-300" /> Enter the code from your authenticator app.
                  </div>
                  <label className="block text-xs uppercase tracking-widest text-mute mb-1.5">6-digit code</label>
                  <input
                    data-testid={`${cfg.testPrefix}-login-otp`}
                    autoFocus inputMode="numeric" maxLength={6} required
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g,''))}
                    className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2.5 mb-5 font-mono tracking-widest text-center text-lg focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                  <button type="submit" disabled={loading || otp.length !== 6}
                    data-testid={`${cfg.testPrefix}-login-otp-submit`}
                    className="w-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 px-4 py-2.5 rounded-md font-medium flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(79,70,229,0.35)]">
                    {loading ? "Verifying…" : (<>Verify & sign in <ArrowRight className="w-4 h-4" /></>)}
                  </button>
                  <button type="button" onClick={() => { setOtpChallenge(false); setOtp(""); }} data-testid="otp-cancel-btn" className="mt-3 w-full text-xs text-mute hover:text-white">Cancel</button>
                </>
              ) : (
                <>
                  <label className="block text-xs uppercase tracking-widest text-mute mb-1.5">Email</label>
                  <input
                    data-testid={`${cfg.testPrefix}-login-email`}
                    autoComplete="email"
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2.5 mb-4 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs uppercase tracking-widest text-mute">Password</label>
                    <Link to="/forgot-password" className="text-[10px] text-mute hover:text-white uppercase tracking-widest" data-testid={`${cfg.testPrefix}-login-forgot-link`}>Forgot?</Link>
                  </div>
                  <input
                    data-testid={`${cfg.testPrefix}-login-password`}
                    autoComplete="current-password"
                    type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2.5 mb-5 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                  <button
                    type="submit" disabled={loading}
                    data-testid={`${cfg.testPrefix}-login-submit`}
                    className="w-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 px-4 py-2.5 rounded-md font-medium flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(79,70,229,0.35)]"
                  >
                    {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
                  </button>
                  {variant === "panel" && (
                    <>
                      <div className="mt-5 flex items-center gap-3 text-[10px] uppercase tracking-widest text-mute">
                        <div className="flex-1 h-px bg-white/10" />
                        <span>or</span>
                        <div className="flex-1 h-px bg-white/10" />
                      </div>
                      <div className="mt-4">
                        <GoogleSignInButton testId="panel-login-google" />
                      </div>
                      <div className="mt-5 text-xs text-mute text-center">
                        New here? <Link to="/panel/register" className="text-white underline" data-testid="panel-login-to-register">Create a panelist account</Link>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
